import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, Box, Typography } from '@mui/material';
import Lottie from 'lottie-react';
import successAnimationData from '../../assets/success-animation.json';

/**
 * Success Animation Component
 * Shows a success animation with optional message
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when dialog should close
 * @param {string} props.message - Success message to display
 * @param {number} props.autoCloseDuration - Duration in ms before auto-closing (default: 2000)
 */
const SuccessAnimation = ({ 
  open, 
  onClose, 
  message = 'Success!', 
  autoCloseDuration = 2000 
}) => {
  const [animationComplete, setAnimationComplete] = useState(false);

  useEffect(() => {
    if (open) {
      setAnimationComplete(false);
      
      // Auto-close after animation completes
      const timer = setTimeout(() => {
        setAnimationComplete(true);
        if (onClose) {
          onClose();
        }
      }, autoCloseDuration);

      return () => clearTimeout(timer);
    }
  }, [open, onClose, autoCloseDuration]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          textAlign: 'center',
          py: 3,
          px: 2,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          border: 'none',
          animation: 'slideInUp 0.3s ease-out'
        }
      }}
      sx={{
        '& .MuiDialog-paper': {
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(50px)',
              opacity: 0
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1
            }
          },
          '@keyframes fadeInUp': {
            '0%': {
              transform: 'translateY(20px)',
              opacity: 0
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1
            }
          }
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          {/* Success Animation */}
          <Box
            sx={{
              width: 120,
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#4caf50',
              borderRadius: '50%',
              boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)'
            }}
          >
            <Lottie
              animationData={successAnimationData}
              loop={false}
              style={{
                width: 80,
                height: 80
              }}
            />
          </Box>

          {/* Success Message */}
          <Typography
            variant="h6"
            sx={{
              color: '#2e7d32',
              fontWeight: 600,
              fontSize: '1.25rem',
              animation: 'fadeInUp 0.5s ease-out 0.3s both'
            }}
          >
            {message}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              fontSize: '0.875rem',
              animation: 'fadeInUp 0.5s ease-out 0.5s both'
            }}
          >
            Invoice saved successfully
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SuccessAnimation;